// Logica del cruscotto (generale e d'impianto): pura, senza query, testata in quadro.test.ts.
import { titoloScadenza, type Periodo, type StatoDichiarazione, type StatoScadenza } from '@praticheenergia/domain'
import { dec } from '@praticheenergia/ad1-engine'
import { valutaScostamento, type MeseSerie } from '../green/scostamento'
import { percento } from '../green/formato'
import type { ImpiantoGreen } from '../green/green.api'
import type { Scadenza } from '../scadenze/scadenze.api'
import { ETICHETTA_PERIODO, ETICHETTA_STATO_DICHIARAZIONE } from '../impianti/stato-dichiarazione'

export type Tono = 'rosso' | 'ambra' | 'verde' | 'grigio'

export const TONO_SCADENZA: Record<StatoScadenza, Tono> = {
  scaduta: 'rosso',
  in_avvicinamento: 'ambra',
  futura: 'verde',
  completata: 'grigio',
}

const GIORNO_MS = 86_400_000
const mezzanotte = (iso: string) => Date.parse(`${iso}T00:00:00Z`)
const isoOggi = (oggi: Date) => oggi.toISOString().slice(0, 10)

export const giorniResidui = (iso: string, oggi: Date = new Date()) =>
  Math.round((mezzanotte(iso) - mezzanotte(isoOggi(oggi))) / GIORNO_MS)

export function testoResidui(giorni: number): string {
  if (giorni === 0) return 'oggi'
  return giorni > 0 ? `tra ${giorni} gg` : `scaduta da ${-giorni} gg`
}

// Da `2026-09-30` a `30/09/2026` senza passare dal fuso orario.
export const dataItaliana = (iso: string) => iso.split('-').reverse().join('/')

/** Giorno da cui parte l'avviso: la scadenza meno il preavviso. */
export const dataAvviso = (iso: string, giorniPreavviso: number) =>
  new Date(mezzanotte(iso) - giorniPreavviso * GIORNO_MS).toISOString().slice(0, 10)

const aperte = (scadenze: readonly Scadenza[]) =>
  scadenze.filter((s) => s.stato !== 'completata').sort((a, b) => a.scadenza.localeCompare(b.scadenza))

export interface Produzione {
  readonly tono: Tono
  readonly testo: string
  readonly anomalia: boolean
}

export function produzioneImpianto(g: ImpiantoGreen | undefined, sogliaPartner: string): Produzione {
  if (g === undefined) return { tono: 'grigio', testo: 'Non monitorato', anomalia: false }
  if (g.statoDistributore === 'credenziali_mancanti') return { tono: 'grigio', testo: 'Credenziali mancanti', anomalia: false }
  const s = valutaScostamento(g.serie, g.soglia ?? sogliaPartner)
  switch (s.stato) {
    case 'anomalia':
      return { tono: 'rosso', testo: `${percento(s.variazioneMese ?? s.variazione)} sotto soglia`, anomalia: true }
    case 'ok':
      return { tono: 'verde', testo: 'In linea', anomalia: false }
    case 'senza_storico':
      return { tono: 'grigio', testo: 'Senza storico', anomalia: false }
    case 'senza_dati':
      return { tono: 'grigio', testo: 'In attesa di dati', anomalia: false }
  }
}

export interface Prossimo {
  readonly titolo: string
  readonly dove: string
  readonly scadenza: string
}

export interface Quadro {
  readonly scadute: number
  readonly inPreavviso: number
  readonly aperte: number
  /** Il prossimo adempimento non ancora scaduto: e' quello che la lancetta misura. */
  readonly prossima: Prossimo | null
  /** Quota di adempimenti aperti non scaduti, da 0 a 100. */
  readonly inRegola: number
  readonly sottoSoglia: number
  readonly credenzialiMancanti: number
}

/**
 * Le dichiarazioni (righe da `righeDichiarazioniElenco`) contano come le scadenze: rosse
 * tra le scadute, ambra in preavviso. Grigie (inviate o senza termine) restano fuori.
 */
export function quadro(
  scadenze: readonly Scadenza[],
  green: readonly ImpiantoGreen[],
  sogliaPartner: string,
  dichiarazioni: readonly RigaTabella[] = [],
): Quadro {
  const daFare = aperte(scadenze)
  const dich = dichiarazioni.filter((d) => d.tono !== 'grigio' && d.scadenza !== null)
  const scadute = daFare.filter((s) => s.stato === 'scaduta').length + dich.filter((d) => d.tono === 'rosso').length
  const totale = daFare.length + dich.length
  const candidati: Prossimo[] = [
    ...daFare.filter((s) => s.stato !== 'scaduta').map((s) => ({ titolo: titoloScadenza(s), dove: s.impianto, scadenza: s.scadenza })),
    ...dich.filter((d) => d.tono !== 'rosso').map((d) => ({ titolo: d.titolo, dove: d.dove ?? '', scadenza: d.scadenza! })),
  ]
  return {
    scadute,
    inPreavviso: daFare.filter((s) => s.stato === 'in_avvicinamento').length + dich.filter((d) => d.tono === 'ambra').length,
    aperte: totale,
    prossima: candidati.sort((a, b) => a.scadenza.localeCompare(b.scadenza))[0] ?? null,
    inRegola: totale === 0 ? 100 : Math.round(((totale - scadute) / totale) * 100),
    sottoSoglia: green.filter((g) => produzioneImpianto(g, sogliaPartner).anomalia).length,
    credenzialiMancanti: green.filter((g) => g.statoDistributore === 'credenziali_mancanti').length,
  }
}

export interface Riepilogo {
  readonly tono: Tono
  readonly testo: string
  readonly prossima: Scadenza | null
}

/** La spia delle scadenze di un impianto: vince la piu' grave. */
export function riepilogoScadenze(scadenze: readonly Scadenza[]): Riepilogo {
  const daFare = aperte(scadenze)
  const prossima = daFare[0] ?? null
  const scadute = daFare.filter((s) => s.stato === 'scaduta').length
  if (scadute > 0) return { tono: 'rosso', testo: scadute === 1 ? 'Scaduta' : `${scadute} scadute`, prossima }
  const preavviso = daFare.filter((s) => s.stato === 'in_avvicinamento').length
  if (preavviso > 0) return { tono: 'ambra', testo: preavviso === 1 ? 'In preavviso' : `${preavviso} in preavviso`, prossima }
  if (prossima) return { tono: 'verde', testo: 'In regola', prossima }
  return { tono: 'grigio', testo: 'Nessuna scadenza', prossima }
}

export function statoDichiarazione(stato: StatoDichiarazione | null): { tono: Tono; testo: string } {
  if (stato === null) return { tono: 'grigio', testo: 'Nessuna' }
  const tono: Tono = stato === 'inviata' ? 'verde' : stato === 'errore_invio' ? 'rosso' : 'ambra'
  return { tono, testo: ETICHETTA_STATO_DICHIARAZIONE[stato] }
}

export interface Attivita {
  readonly chiave: string
  readonly tono: 'rosso' | 'ambra'
  /** Null per le attivita' che non hanno una data (invio rifiutato, produzione). */
  readonly giorni: number | null
  readonly titolo: string
  readonly dettaglio: string
  readonly azione: string
  /** Relativa alla radice del partner. */
  readonly rotta: string
}


/** Prima cio' che e' in ritardo o rifiutato, poi la produzione, infine il preavviso; per giorni residui. */
export function daFare(
  scadenze: readonly Scadenza[],
  dichiarazioni: readonly RigaTabella[],
  green: readonly ImpiantoGreen[],
  sogliaPartner: string,
  oggi: Date = new Date(),
): Attivita[] {
  const daScadenza = (s: Scadenza): Attivita => ({
    chiave: `s${s.id}`,
    tono: s.stato === 'scaduta' ? 'rosso' : 'ambra',
    giorni: giorniResidui(s.scadenza, oggi),
    titolo: titoloScadenza(s),
    dettaglio: `${s.impianto} · ${dataItaliana(s.scadenza)}`,
    azione: 'Gestisci',
    rotta: 'scadenzario',
  })
  const daDichiarazione = (d: RigaTabella): Attivita => ({
    chiave: d.chiave,
    tono: d.tono === 'rosso' ? 'rosso' : 'ambra',
    giorni: d.scadenza === null ? null : giorniResidui(d.scadenza, oggi),
    titolo: d.titolo,
    dettaglio: [d.dove, d.nota, d.scadenza && `termine ${dataItaliana(d.scadenza)}`].filter(Boolean).join(' · '),
    azione: 'Apri',
    rotta: d.rotta ?? 'dichiarazioni',
  })
  const perGiorni = (a: Attivita, b: Attivita) => (a.giorni ?? Infinity) - (b.giorni ?? Infinity)
  const urgenti = aperte(scadenze).filter((s) => s.stato === 'scaduta' || s.stato === 'in_avvicinamento')
  const tutte = [
    ...urgenti.map(daScadenza),
    ...dichiarazioni.filter((d) => d.tono === 'rosso' || d.tono === 'ambra').map(daDichiarazione),
  ]
  const anomalie: Attivita[] = green.flatMap((g) => {
    const p = produzioneImpianto(g, sogliaPartner)
    return p.anomalia
      ? [{ chiave: `g${g.id}`, tono: 'rosso' as const, giorni: null, titolo: `Produzione ${p.testo}`, dettaglio: `${g.codiceImpianto} · ${g.ragioneSociale}`, azione: 'Vedi', rotta: `impianti-green/${g.id}` }]
      : []
  })
  return [
    ...tutte.filter((a) => a.tono === 'rosso').sort(perGiorni),
    ...anomalie,
    ...tutte.filter((a) => a.tono === 'ambra').sort(perGiorni),
  ]
}

export interface MeseConfronto {
  readonly anno: number
  readonly mese: number
  readonly attuale: string | null
  readonly precedente: string | null
  /** Sotto l'anno prima di piu' della soglia: la barra si colora. */
  readonly sotto: boolean
}

/** Gli ultimi 12 mesi con dati, ciascuno accanto allo stesso mese dell'anno prima. */
export function confrontoMensile(serie: readonly MeseSerie[], soglia: string): MeseConfronto[] {
  const valore = new Map(serie.map((m) => [m.anno * 12 + m.mese - 1, m.produzione]))
  const conDati = serie.filter((m) => m.produzione !== null).map((m) => m.anno * 12 + m.mese - 1)
  if (conDati.length === 0) return []
  const ultimo = Math.max(...conDati)
  const limite = dec(1).minus(dec(soglia).div(100))
  return Array.from({ length: 12 }, (_, i) => {
    const k = ultimo - 11 + i
    const attuale = valore.get(k) ?? null
    const precedente = valore.get(k - 12) ?? null
    const sotto =
      attuale !== null && precedente !== null && !dec(precedente).isZero() && dec(attuale).lessThan(dec(precedente).times(limite))
    return { anno: Math.floor(k / 12), mese: (k % 12) + 1, attuale, precedente, sotto }
  })
}

export interface Dichiarazioni {
  readonly tono: Tono | null
  readonly testo: string
}

/** La spia delle dichiarazioni dell'anno: spenta quando sono tutte inviate. */
export function spiaDichiarazioni(dichiarazioni: readonly { periodo: Periodo; stato: StatoDichiarazione }[]): Dichiarazioni {
  const errore = dichiarazioni.find((d) => d.stato === 'errore_invio')
  if (errore) return { tono: 'rosso', testo: `Errore ${ETICHETTA_PERIODO[errore.periodo]}` }
  const aperta = dichiarazioni.find((d) => d.stato !== 'inviata')
  if (aperta) return { tono: 'ambra', testo: `${ETICHETTA_STATO_DICHIARAZIONE[aperta.stato]} · ${ETICHETTA_PERIODO[aperta.periodo]}` }
  return { tono: null, testo: dichiarazioni.length === 0 ? 'Nessuna' : 'Inviate' }
}

export interface RigaAdempimento {
  readonly scadenza: Scadenza
  /** L'ultima volta che l'adempimento e' stato fatto, dalle scadenze completate. */
  readonly ultimoRinnovo: string | null
}

/**
 * Le righe dello scadenzario d'impianto, come nella scheda Excel: una per adempimento.
 * Un adempimento che si ripete lascia dietro di se' le scadenze completate: si mostra la
 * aperta, e delle completate resta solo la data dell'ultimo rinnovo. Una tantum: ogni
 * scadenza e' una riga sua (le soglie del CCI).
 */
export function righeAdempimenti(scadenze: readonly Scadenza[]): RigaAdempimento[] {
  const gruppi = new Map<string, Scadenza[]>()
  for (const s of scadenze) {
    const chiave = s.periodicitaMesi === null && s.completataIl === null ? `u${s.id}` : `${s.tipo}|${s.titolo ?? ''}`
    gruppi.set(chiave, [...(gruppi.get(chiave) ?? []), s])
  }
  return [...gruppi.values()].flatMap((g) => {
    const fatte = g.filter((s) => s.completataIl !== null).map((s) => s.completataIl!).sort()
    const ultimoRinnovo = fatte.at(-1) ?? null
    const aperte = g.filter((s) => s.completataIl === null)
    const ultimaFatta = g.filter((s) => s.completataIl === ultimoRinnovo).at(-1)
    return (aperte.length > 0 ? aperte : ultimaFatta ? [ultimaFatta] : []).map((scadenza) => ({ scadenza, ultimoRinnovo }))
  })
}

/** Una riga della tabella adempimenti d'impianto, che venga da una scadenza o da una dichiarazione. */
export interface RigaTabella {
  readonly chiave: string
  readonly ente: string
  readonly titolo: string
  readonly nota: string
  readonly periodicita: string
  readonly ultimoRinnovo: string | null
  readonly scadenza: string | null
  readonly tono: Tono
  readonly residui: string
  readonly avviso: string | null
  readonly preavviso: number | null
  /** Solo nel cruscotto generale: impianto e cliente, e dove si apre (relativa al partner). */
  readonly dove?: string
  readonly rotta?: string
}

// Soglia di attenzione per le dichiarazioni: non e' una regola fiscale, solo quando la riga
// diventa ambra. Come il preavviso predefinito delle scadenze.
const PREAVVISO_DICHIARAZIONE = 30

/** Le dichiarazioni dell'anno come righe ADM: termine dalla regola fiscale, stato dal portale. */
export function righeDichiarazioni(
  dichiarazioni: readonly { periodo: Periodo; stato: StatoDichiarazione }[],
  termini: Partial<Record<Periodo, string>>,
  anno: number,
  oggi: Date = new Date(),
): RigaTabella[] {
  return dichiarazioni.map((d) => {
    const termine = termini[d.periodo] ?? null
    const giorni = termine === null ? null : giorniResidui(termine, oggi)
    const inviata = d.stato === 'inviata'
    // Un invio rifiutato va rettificato subito, qualunque sia il termine.
    const tono: Tono =
      d.stato === 'errore_invio' ? 'rosso'
      : inviata || giorni === null ? 'grigio'
      : giorni < 0 ? 'rosso'
      : giorni <= PREAVVISO_DICHIARAZIONE ? 'ambra'
      : 'verde'
    return {
      chiave: `d-${d.periodo}`,
      ente: 'ADM',
      titolo: `Dichiarazione di consumo · ${ETICHETTA_PERIODO[d.periodo]} ${anno}`,
      nota: ETICHETTA_STATO_DICHIARAZIONE[d.stato],
      periodicita: d.periodo === 'annuale' ? 'Annuale' : 'Semestrale',
      ultimoRinnovo: null,
      scadenza: termine,
      tono,
      residui: inviata ? 'Inviata' : giorni === null ? 'Termine non indicato' : testoResidui(giorni),
      avviso: inviata || termine === null ? null : dataAvviso(termine, PREAVVISO_DICHIARAZIONE),
      preavviso: inviata || termine === null ? null : PREAVVISO_DICHIARAZIONE,
    }
  })
}

/** Le dichiarazioni di tutti gli impianti, come righe con impianto e collegamento. */
export function righeDichiarazioniElenco(
  elenco: readonly { id: number; impiantoId: number; impianto: string; cliente: string; periodo: Periodo; stato: StatoDichiarazione }[],
  termini: Partial<Record<Periodo, string>>,
  anno: number,
  oggi: Date = new Date(),
): RigaTabella[] {
  return elenco.map((d) => ({
    ...righeDichiarazioni([d], termini, anno, oggi)[0]!,
    chiave: `d${d.id}`,
    dove: `${d.impianto} · ${d.cliente}`,
    rotta: `impianti/${d.impiantoId}/dichiarazione/${anno}/${d.periodo}`,
  }))
}

/**
 * La spia «Dichiarazioni» di un impianto nel cruscotto generale: vince la piu' urgente
 * delle sue dichiarazioni dell'anno, non l'ultima aperta.
 */
export function riepilogoDichiarazioni(
  elenco: readonly { periodo: Periodo; stato: StatoDichiarazione }[],
  termini: Partial<Record<Periodo, string>>,
  anno: number,
  oggi: Date = new Date(),
): { tono: Tono; testo: string } {
  if (elenco.length === 0) return { tono: 'grigio', testo: 'Nessuna' }
  const righe = righeDichiarazioni(elenco, termini, anno, oggi).map((r, i) => ({ r, d: elenco[i]! }))
  const errore = righe.find(({ d }) => d.stato === 'errore_invio')
  if (errore) return { tono: 'rosso', testo: `Errore di invio · ${ETICHETTA_PERIODO[errore.d.periodo]}` }
  const ritardo = righe.find(({ r }) => r.tono === 'rosso')
  if (ritardo) return { tono: 'rosso', testo: `In ritardo · ${ETICHETTA_PERIODO[ritardo.d.periodo]}` }
  const aperta = righe
    .filter(({ d }) => d.stato !== 'inviata')
    .sort((a, b) => (a.r.scadenza ?? '9999').localeCompare(b.r.scadenza ?? '9999'))[0]
  if (aperta) return { tono: 'ambra', testo: `${ETICHETTA_STATO_DICHIARAZIONE[aperta.d.stato]} · ${ETICHETTA_PERIODO[aperta.d.periodo]}` }
  return { tono: 'verde', testo: 'Inviate' }
}
